import type { ChangeEventHandler, Ref } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import avatar_1 from '../../assets/common/avatar_1.webp'
import avatar_2 from '../../assets/common/avatar_2.webp'
import avatar_3 from '../../assets/common/avatar_3.webp'
import avatar_4 from '../../assets/common/avatar_4.webp'
import avatar_5 from '../../assets/common/avatar_5.webp'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import Icon from '../icon'
import { TailSpin } from '../loaders'

interface ChooseAvatarProps {
  isVisible: boolean
  onSelectFile: ChangeEventHandler<HTMLInputElement>
  fileRef: Ref<HTMLInputElement>
}

const ChooseAvatar = ({
  isVisible,
  onSelectFile,
  fileRef,
}: ChooseAvatarProps) => {
  const setProfilePicture = useBoundStore(state => state.user.setProfilePicture)
  const { t } = useTranslation('personalData')
  const AVATARS = [avatar_1, avatar_2, avatar_3, avatar_4, avatar_5]
  const [isLoading, setIsLoading] = useState<number | null>(null)

  const onUpload = async (src: string, index: number) => {
    try {
      const image = document.createElement('img')
      image.src = src
      const canvas = document.createElement('canvas')
      canvas.width = image.naturalWidth
      canvas.height = image.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      setIsLoading(index)
      await new Promise(resolve => {
        image.onload = () => {
          ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight)
          resolve(null)
        }
      })

      const blob = await new Promise(resolve => {
        canvas.toBlob(blob => {
          resolve(blob)
        }, 'image/png')
      })

      if (!blob) return

      const response = await api.uploadMedia<ApiResponse<string>>(
        '/ajax_data.php',
        {
          file: blob as File,
          type: 'upload_pictures',
        },
      )

      const { status } = response.data
      if (status === 1) {
        const response = await api.post<ApiResponse<string>>('/ajax_data.php', {
          type: 'get_imgurl',
        })
        const { status, info } = response.data
        if (status === 1) {
          setProfilePicture(info)
        }
      }
    } catch (e) {
      console.error(e)
    }
    setIsLoading(null)
  }

  return (
    <div
      className={classNames(
        'absolute w-[280px] bg-[rgba(81,81,81,0.9)] z-[5] origin-[top_center] transition-all duration-[0.2s] ease-[ease-in-out] px-[15px] py-5 rounded-[10px] left-[25px] ',
        'top-[140px] xl:grid-cols-[repeat(6,1fr)] xl:w-max xl:origin-left xl:bg-[rgba(37,37,37,0.9)] xl:p-4 xl:left-[200px] xl:top-8',
        isVisible
          ? 'opacity-100 pointer-events-auto scale-100'
          : 'opacity-0 pointer-events-none scale-50',
      )}>
      <p className="text-white text-sm font-Avenir-Heavy mb-4 xl:text-base xl:mb-2.5">
        {t('choose-your-avatar')}
      </p>
      <div className="grid grid-cols-[1fr_1fr_1fr] gap-5 xl:grid-cols-[repeat(6,1fr)]">
        {AVATARS.map((avatar, index) =>
          isLoading === index ? (
            <div
              key={index}
              className="flx-ctr w-[70px] h-[70px] bg-[#434343] relative justify-self-center cursor-pointer rounded-[50%] border-solid border-white bg-transparent border-0">
              <TailSpin width={30} height={30} color="#fff" />
            </div>
          ) : (
            <div
              className="w-[70px] h-[70px] bg-[#434343] relative justify-self-center cursor-pointer rounded-[50%] border-2 border-solid border-white"
              key={index}
              onClick={() => onUpload(avatar, index)}>
              <img className="w-full h-full object-cover" src={avatar} />
            </div>
          ),
        )}
        <div className="flx-ctr w-[70px] h-[70px] bg-[#434343] relative justify-self-center cursor-pointer rounded-[50%] border-2 border-solid border-white">
          <Icon id="camera" width="30" height="24" fill="#9A9A9A" />
          <input
            className="absolute w-full h-full z-[1] opacity-0 cursor-pointer left-0 top-0"
            ref={fileRef}
            type="file"
            accept=".jpg,.png,.jpeg"
            onChange={onSelectFile}
          />
        </div>
      </div>
    </div>
  )
}

export default ChooseAvatar
